import { Add, Map as MapIcon, Print, ViewList } from '@mui/icons-material';
import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { eachDayOfInterval, format } from 'date-fns';
import React, { useMemo, useState } from 'react';
import {
    useAddItineraryItemMutation,
    useCreateBookingSetMenuMutation,
    useCreateJobRequestMutation,
} from '../../../../../app/Slices/SevenDayApiSlice';
import {
    JobRequest,
    ProviderType,
} from '../../../../../app/Slices/SevenDayApiTypes';
import UserRole from '../../../../../shared/AuthRoles';
import { theme } from '../../../../../shared/theme';
import { ItineraryItemType } from '../../../../enums/itinerary';
import toast from '../../../../shared/Toast';
import {
    ItineraryItemDto,
    ItineraryItemPayload,
} from '../../../../types/itinerary';
import { CreateBookingSetMenu } from '../../../../types/set-menu';
import { ViewKey } from '../ViewIndividualReservation';
import ItineraryItemCard from './ItineraryItemCard';
import ItineraryItemForm, {
    ItineraryItemFormValues,
} from './ItineraryItemForm';
import ItineraryMap from './ItineraryMap';

const groupByDay = (
    items: ItineraryItemDto[],
    bookingStart: string,
    bookingEnd: string,
): { date: string; items: ItineraryItemDto[] }[] => {
    const dayMap = new Map<string, ItineraryItemDto[]>();

    for (const day of eachDayOfInterval({
        start: new Date(bookingStart),
        end: new Date(bookingEnd),
    })) {
        dayMap.set(format(day, 'yyyy-MM-dd'), []);
    }

    for (const item of items) {
        const days = eachDayOfInterval({
            start: new Date(item.startDate),
            end: new Date(item.endDate),
        });

        for (const day of days) {
            const key = format(day, 'yyyy-MM-dd');
            const targetDay = dayMap.get(key);
            if (targetDay) {
                targetDay.push(item);
            } else {
                dayMap.set(key, [item]);
            }
        }
    }

    return Array.from(dayMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, items]) => ({
            date,
            items,
        }));
};

export default function ItineraryTab({
    reservation,
    handleOpenItineraryItem,
    showItineraryForm,
    setShowItineraryForm,
    isAddOnForm,
    setIsAddOnForm,
    hasAddOns,
    setTab,
    originalTab,
    preselectedAddOn,
    setPreselectedAddOn,
    preselectedPartnerService,
    setPreselectedPartnerService,
}: {
    reservation: any;
    handleOpenItineraryItem: (bookingId: number) => void;
    showItineraryForm: boolean;
    setShowItineraryForm: (show: boolean) => void;
    isAddOnForm: boolean;
    setIsAddOnForm: (show: boolean) => void;
    hasAddOns: boolean;
    setTab: (tab: ViewKey) => void;
    originalTab: ViewKey;
    preselectedAddOn: { id: number; type: string } | null;
    setPreselectedAddOn: (addOn: { id: number; type: string } | null) => void;
    preselectedPartnerService: boolean;
    setPreselectedPartnerService: (selected: boolean) => void;
}) {
    const loggedInUser = UserRole();
    const isOwner = loggedInUser?.role?.includes('owner');
    const isMobile = useMediaQuery('(max-width: 550px)');
    const isTablet = useMediaQuery('(max-width: 770px)');
    const [addItemDate, setAddItemDate] = useState<string | null>(null);
    const [showMap, setShowMap] = useState(false);
    const [addItineraryItem, { isLoading: isAdding }] =
        useAddItineraryItemMutation();
    const [selectedItem, setSelectedItem] = useState<ItineraryItemDto | null>(
        null,
    );
    const [createJob] = useCreateJobRequestMutation();
    const [createBookingSetMenu] = useCreateBookingSetMenuMutation();
    const isSmallItinerary = isMobile || showMap;

    const items = reservation?.itineraryItems;
    const hasItineraryItems = Array.isArray(items) && items.length > 0;

    const dayGroups = groupByDay(
        items,
        reservation?.startDate,
        reservation?.endDate,
    );

    const handlePrintItinerary = () => {
        const fmtDate = (d?: string) =>
            d ? format(new Date(d), 'EEE d MMM yyyy') : '';
        const joinNonEmpty = (parts: Array<string | undefined>) =>
            parts
                .map((p) => String(p ?? '').trim())
                .filter(Boolean)
                .join(', ');
        const fullName = (u: any) =>
            joinNonEmpty([u?.firstName, u?.lastName]) || undefined;

        const experienceName =
            reservation?.module?.name || reservation?.moduleName || undefined;
        const experienceType =
            reservation?.module?.type ||
            reservation?.bookingInfo?.moduleType ||
            undefined;
        const location = joinNonEmpty([
            reservation?.module?.streetAddress,
            reservation?.module?.suburb,
            reservation?.module?.state,
            reservation?.module?.countryCode,
        ]);
        const company =
            reservation?.guest?.name ||
            reservation?.company?.name ||
            reservation?.bookingInfo?.companyName ||
            undefined;
        const primaryGuest =
            fullName(reservation?.booker) ||
            fullName(reservation?.guestUser) ||
            reservation?.bookingInfo?.name ||
            undefined;
        const reservationMadeBy =
            fullName(reservation?.createdBy) ||
            fullName(reservation?.booker) ||
            undefined;
        const visitType = reservation?.bookingInfo?.visitType
            ? String(reservation.bookingInfo.visitType)
            : undefined;
        const guestsAttending =
            reservation?.bookingInfo?.guests ??
            reservation?.bookingInfo?.guestCount ??
            reservation?.bookingInfo?.attendees ??
            undefined;

        const checkinDate = fmtDate(reservation?.startDate);
        const checkoutDate = fmtDate(reservation?.endDate);
        const checkinTime = reservation?.bookingInfo?.checkinTime || '';
        const checkoutTime = reservation?.bookingInfo?.checkoutTime || '';
        const checkinText = [checkinDate, checkinTime]
            .filter(Boolean)
            .join(' ');
        const checkoutText = [checkoutDate, checkoutTime]
            .filter(Boolean)
            .join(' ');

        const summaryRows = [
            {
                label: 'Reservation status',
                value: reservation?.status
                    ? String(reservation.status)
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (c) => c.toUpperCase())
                    : undefined,
            },
            { label: 'Experience', value: experienceName },
            { label: 'Type', value: experienceType },
            { label: 'Location', value: location || undefined },
            { label: 'Company', value: company },
            { label: 'Primary guest', value: primaryGuest },
            { label: 'Reservation made by', value: reservationMadeBy },
            { label: 'Visit type', value: visitType },
            {
                label: 'Guests attending',
                value:
                    guestsAttending !== undefined && guestsAttending !== null
                        ? String(guestsAttending)
                        : undefined,
            },
            { label: 'Check-in', value: checkinText || undefined },
            { label: 'Check-out', value: checkoutText || undefined },
        ].filter((row) => row.value);

        const summaryHtml = summaryRows.length
            ? `<section class="summary">
                <h3>Reservation Summary</h3>
                <div class="summary-grid">
                    ${summaryRows
                        .map(
                            (row) =>
                                `<div class="summary-row"><span class="label">${row.label}:</span> <span class="value">${row.value}</span></div>`,
                        )
                        .join('')}
                </div>
            </section>`
            : '';

        const printBody = dayGroups
            .map((group) => {
                const date = new Date(group.date);
                const dateLabel = format(date, 'EEE d MMM yyyy');
                const rows =
                    group.items.length === 0
                        ? `<li class="muted">No plans for this day</li>`
                        : group.items
                              .map((item: any) => {
                                  const type =
                                      item?.itineraryItemInfo?.itemType ||
                                      'Item';
                                  const title =
                                      item?.itineraryItemInfo?.title ||
                                      item?.itineraryItemInfo?.restaurantName ||
                                      item?.itineraryItemInfo?.provider ||
                                      item?.itineraryItemInfo?.airline ||
                                      item?.moduleName ||
                                      'Itinerary item';
                                  const time = `${item?.startTime || ''}${item?.startTime && item?.endTime ? ' - ' : ''}${item?.endTime || ''}`;
                                  const statusRaw =
                                      item?.status ||
                                      item?.itineraryItemInfo?.status ||
                                      item?.itineraryItemInfo?.bookingStatus ||
                                      undefined;
                                  const statusText = statusRaw
                                      ? String(statusRaw)
                                            .replace(/_/g, ' ')
                                            .replace(/\b\w/g, (c) =>
                                                c.toUpperCase(),
                                            )
                                      : '';
                                  return `<li><strong>${type}</strong> - ${title}${time ? ` <span class="muted">(${time})</span>` : ''}${statusText ? ` <span class="status">[${statusText}]</span>` : ''}</li>`;
                              })
                              .join('');

                return `<section><h3>${dateLabel}</h3><ul>${rows}</ul></section>`;
            })
            .join('');

        const printFrame = document.createElement('iframe');
        printFrame.style.position = 'fixed';
        printFrame.style.right = '0';
        printFrame.style.bottom = '0';
        printFrame.style.width = '0';
        printFrame.style.height = '0';
        printFrame.style.border = '0';
        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow?.document;
        const frameWindow = printFrame.contentWindow;
        if (!frameDoc || !frameWindow) {
            printFrame.remove();
            return;
        }

        frameDoc.open();
        frameDoc.write(`
            <html>
            <head>
                <title>Reservation Itinerary</title>
                <style>
                    body { font-family: Inter, Arial, sans-serif; margin: 24px; color: #1f3550; }
                    h1 { margin: 0 0 8px 0; font-size: 24px; }
                    h2 { margin: 0 0 24px 0; font-size: 14px; font-weight: 500; color: #5f7489; }
                    h3 { margin: 18px 0 8px 0; font-size: 15px; }
                    ul { margin: 0 0 8px 0; padding-left: 18px; }
                    li { margin: 6px 0; line-height: 1.4; }
                    .muted { color: #6f8397; font-style: italic; }
                    .status { color: #35506b; font-weight: 700; }
                    .summary { margin: 0 0 18px 0; padding: 12px 14px; border: 1px solid #d7e0e8; border-radius: 8px; }
                    .summary h3 { margin-top: 0; }
                    .summary-grid { display: grid; grid-template-columns: 1fr; gap: 6px; }
                    .summary-row .label { font-weight: 700; }
                </style>
            </head>
            <body>
                <h1>Reservation Itinerary</h1>
                <h2>#${reservation?.id ?? ''}</h2>
                ${summaryHtml}
                ${printBody}
            </body>
            </html>
        `);
        frameDoc.close();

        const cleanup = () => {
            setTimeout(() => {
                printFrame.remove();
            }, 0);
        };

        frameWindow.onafterprint = cleanup;
        setTimeout(() => {
            frameWindow.focus();
            frameWindow.print();
        }, 150);

        // Fallback cleanup in case onafterprint doesn't fire.
        setTimeout(cleanup, 2000);
    };

    const handleAdd = async (
        payload: ItineraryItemPayload | ItineraryItemFormValues,
    ) => {
        try {
            if (isOwner) {
                toast.error('Owners cannot add itinerary items');
                setShowItineraryForm(false);
                setPreselectedAddOn(null);
                setPreselectedPartnerService(false);
            } else if (
                payload.itineraryItemInfo.itemType ===
                    ItineraryItemType.PartnerService &&
                payload.jobs
            ) {
                if (payload.jobs.providerType === ProviderType.Contractor) {
                    await createJob({
                        ...payload.jobs,
                        booking: { id: reservation.id },
                    } as JobRequest).unwrap();
                } else {
                    await createBookingSetMenu({
                        ...payload.jobs,
                        booking: { id: reservation.id },
                    } as JobRequest).unwrap();
                }
            } else {
                await addItineraryItem([
                    reservation.id,
                    payload as ItineraryItemPayload,
                ]).unwrap();
            }

            toast.success('Itinerary item added');
            setShowItineraryForm(false);
            setPreselectedAddOn(null);
            setPreselectedPartnerService(false);
        } catch (error: any) {
            toast.error(error?.data?.message || 'Failed to add itinerary item');
        }
    };

    const mainBookingLocation = useMemo(() => {
        const module = reservation?.module;
        if (!module?.lat || !module?.lng) return null;
        return {
            lat: module.lat as number,
            lng: module.lng as number,
            name: module.name as string | undefined,
            address: [
                module.streetAddress,
                module.suburb,
                module.state,
                module.countryCode,
            ]
                .filter(Boolean)
                .join(', '),
        };
    }, [reservation?.module]);

    const onClose = (targetTab?: ViewKey) => {
        const isAutomaticModal =
            !preselectedAddOn && !preselectedPartnerService;
        setShowItineraryForm(false);
        setAddItemDate(null);
        setPreselectedAddOn(null);
        setPreselectedPartnerService(false);

        if (targetTab) {
            setTab(targetTab);
        } else if (isAddOnForm) {
            if (isAutomaticModal) {
                setTab('details');
            } else {
                setTab(originalTab);
            }
        }
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2,
                    gap: 1,
                }}
            >
                <Typography variant='h2' sx={{ color: 'text.secondary' }}>
                    Itinerary
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ToggleButtonGroup
                        size='small'
                        exclusive
                        value={showMap}
                        onChange={(_, v) => {
                            setShowMap(v);
                        }}
                    >
                        <ToggleButton value={false} sx={{ px: 1, py: 0.5 }}>
                            <ViewList sx={{ fontSize: 18 }} />
                        </ToggleButton>
                        <ToggleButton value={true} sx={{ px: 1, py: 0.5 }}>
                            <MapIcon sx={{ fontSize: 18 }} />
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {!isOwner && (
                        <Button
                            size='small'
                            variant='outlined'
                            startIcon={<Add />}
                            onClick={() => {
                                setAddItemDate(null);
                                setPreselectedAddOn(null);
                                setPreselectedPartnerService(false);
                                setShowItineraryForm(true);
                                setIsAddOnForm(false);
                            }}
                            sx={{
                                borderRadius: 2,
                                color: theme.palette.primary.main,
                            }}
                        >
                            Add {isMobile ? '' : 'to itinerary'}
                        </Button>
                    )}
                    {hasItineraryItems && (
                        <Button
                            size='small'
                            variant='outlined'
                            startIcon={<Print />}
                            onClick={handlePrintItinerary}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                color: theme.palette.text.primary,
                            }}
                        >
                            Print
                        </Button>
                    )}
                </Box>
            </Box>

            <ItineraryItemForm
                open={showItineraryForm && !isOwner}
                onClose={onClose}
                onSubmit={handleAdd}
                initialDate={addItemDate}
                mainBooking={reservation}
                isAddOnForm={isAddOnForm}
                hasAddOns={hasAddOns}
                setTab={setTab}
                preselectedAddOn={preselectedAddOn}
                preselectedPartnerService={preselectedPartnerService}
            />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isTablet ? 'column' : 'row',
                    gap: isTablet ? 1 : 0,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        width: '100%',
                        maxWidth: showMap && !isTablet ? '300px' : '700px',
                        maxHeight: showMap ? '360px' : 'auto',
                        overflowY: 'auto',
                        borderRadius: 2,
                        '&::-webkit-scrollbar': {
                            width: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: theme.palette.form.background,
                            borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: theme.palette.custom.grey,
                            borderRadius: 999,
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            backgroundColor: theme.palette.text.secondary,
                        },
                        pr: showMap ? 1 : 0,
                    }}
                >
                    {dayGroups.map((group, index) => {
                        const date = new Date(group.date);
                        const daySectionLabel = format(date, 'yyyy-MM-dd');
                        const dayOfTheWeek = format(date, 'EEE').toUpperCase();
                        const month = format(date, 'MMM').toUpperCase();
                        const dayOfTheMonth = format(date, 'd');

                        return (
                            <Box
                                key={group.date}
                                component='section'
                                aria-label={daySectionLabel}
                                sx={{
                                    display: 'flex',
                                    flexDirection: isSmallItinerary
                                        ? 'column'
                                        : 'row',
                                    gap: isSmallItinerary ? 1.5 : 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: isSmallItinerary
                                            ? 'row'
                                            : 'column',
                                        justifyContent: 'space-around',
                                        alignItems: 'center',
                                        bgcolor: theme.palette.form.background,
                                        width: isSmallItinerary ? 100 : 140,
                                        height: isSmallItinerary ? 'auto' : 95,
                                        borderRadius: 2,
                                        p: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 300,
                                            fontSize: isSmallItinerary
                                                ? 12
                                                : 16,
                                        }}
                                    >
                                        {dayOfTheWeek}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontSize: isSmallItinerary
                                                ? 12
                                                : 16,
                                            fontWeight: 700,
                                        }}
                                    >
                                        {dayOfTheMonth}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: 300,
                                            fontSize: isSmallItinerary
                                                ? 12
                                                : 16,
                                        }}
                                    >
                                        {month}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.25,
                                        width: '100%',
                                        maxWidth: '450px',
                                        position: 'relative',
                                        '&::before':
                                            group.items.length > 1
                                                ? {
                                                      content: '""',
                                                      position: 'absolute',
                                                      left: 16,
                                                      top: 0,
                                                      bottom: 0,
                                                      width: '2px',
                                                      bgcolor:
                                                          theme.palette.custom
                                                              .grey,
                                                  }
                                                : {},
                                    }}
                                >
                                    {group.items.length === 0 ? (
                                        <Typography
                                            variant='body2'
                                            sx={{
                                                color: theme.palette.text
                                                    .secondary,
                                                fontStyle: 'italic',
                                                py: 0.75,
                                            }}
                                        >
                                            No plans for this day
                                        </Typography>
                                    ) : (
                                        group.items.map((item, index) => (
                                            <ItineraryItemCard
                                                handleOpenItineraryItem={
                                                    handleOpenItineraryItem
                                                }
                                                key={item.id ?? index}
                                                label={`${daySectionLabel}-${index}`}
                                                item={item}
                                                isLast={
                                                    index ===
                                                    group.items.length - 1
                                                }
                                                isFirst={index === 0}
                                                showMap={showMap}
                                                setSelectedItem={
                                                    setSelectedItem
                                                }
                                                reservation={reservation}
                                            />
                                        ))
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
                {showMap && (
                    <ItineraryMap
                        reservation={reservation}
                        items={items}
                        selectedItem={selectedItem}
                    />
                )}
            </Box>
        </Box>
    );
}
